import { useEffect, useRef } from 'react'
import * as accountApi from '../api/account'
import { kluczKraju } from '../kraj'

// Klucz w pamięci przeglądarki, pod którym trzymane są obserwowane (SelectionContext).
const FAVORITES_KEY = kluczKraju('voltvalue_favorites')
// Znacznik: dla którego konta przenieśliśmy już dane z przeglądarki na serwer.
const SYNCED_KEY = kluczKraju('voltvalue_favorites_synced_for')

/**
 * Przenosi obserwowane z pamięci przeglądarki na konto — RAZ, po zalogowaniu.
 *
 * Dlaczego to w ogóle istnieje: przed wprowadzeniem kont obserwowane leżały
 * wyłącznie w przeglądarce. Gdyby po zalogowaniu po prostu zaczęły się liczyć
 * te z serwera, użytkownik zobaczyłby PUSTĄ listę i uznał, że stracił dane.
 *
 * Zasady:
 *  * DOKŁADAMY, nie nadpisujemy — użytkownik mógł obserwować coś na innym
 *    urządzeniu i utrata tego byłaby dla niego niezrozumiała,
 *  * po udanym przeniesieniu NIE kasujemy danych z przeglądarki — gdyby coś
 *    poszło nie tak po stronie serwera, użytkownik nadal je ma,
 *  * znacznik jest per konto, więc przelogowanie na inne konto przenosi ponownie.
 */
export const useObservedSync = (isAuthenticated, userEmail) => {
  const wTrakcie = useRef(false)

  useEffect(() => {
    if (!isAuthenticated || !userEmail || wTrakcie.current) return
    if (localStorage.getItem(SYNCED_KEY) === userEmail) return

    let raw
    try {
      raw = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]')
    } catch {
      raw = []
    }
    if (!Array.isArray(raw) || raw.length === 0) {
      // Nie ma czego przenosić — zapamiętujemy to, żeby nie sprawdzać
      // przy każdym renderze.
      localStorage.setItem(SYNCED_KEY, userEmail)
      return
    }

    // Format w przeglądarce: [[klucz, { product, productType }], ...]
    const items = raw
      .map(([, wpis]) => wpis)
      .filter((w) => w && w.product && w.productType)
      .map((w) => ({
        category: w.productType,
        product_id: w.product.id,
        model_id: w.product.model_id ?? null,
      }))
      .filter((x) => Number.isInteger(x.product_id))

    if (items.length === 0) {
      localStorage.setItem(SYNCED_KEY, userEmail)
      return
    }

    wTrakcie.current = true
    accountApi.syncObserved(items)
      .then((res) => {
        // Znacznik stawiamy TYLKO po powodzeniu. Przy błędzie spróbujemy
        // ponownie przy następnym wejściu, zamiast po cichu zgubić dane.
        if (res.ok) localStorage.setItem(SYNCED_KEY, userEmail)
      })
      .finally(() => { wTrakcie.current = false })
  }, [isAuthenticated, userEmail])
}

export default useObservedSync
